import React from "react";

import { emergencyAlertsApi, type EmergencyAlertResponse } from "../../lib/api/handlers/emergencyAlerts";
import { useFacilityAccessOrNull } from "../../lib/FacilityAccessContext";

export type NotificationSeverity = "info" | "success" | "warning" | "critical";

export type NotificationPayload = {
  id?: string;
  title: string;
  message: string;
  severity?: NotificationSeverity;
  category?: string;
  metadata?: Record<string, unknown>;
  href?: string;
  triggeredAt?: string | Date;
};

export type NotificationRecord = NotificationPayload & {
  id: string;
  timestamp: string;
  read: boolean;
};

type NotificationCenterValue = {
  notifications: NotificationRecord[];
  unreadCount: number;
  push: (payload: NotificationPayload) => NotificationRecord;
  markAllRead: () => void;
  markRead: (id: string) => void;
  remove: (id: string) => void;
};

type NotificationAction =
  | { type: "push"; notification: NotificationRecord }
  | { type: "mark-all-read" }
  | { type: "mark-read"; id: string }
  | { type: "remove"; id: string }
  | { type: "hydrate"; notifications: NotificationRecord[] };

const NotificationCenterContext = React.createContext<NotificationCenterValue | undefined>(undefined);

const STORAGE_KEY = "phmis.notification-center";
const MAX_NOTIFICATIONS = 100;
const ALLOWED_SEVERITIES: NotificationSeverity[] = ["info", "success", "warning", "critical"];

function normaliseSeverity(value: unknown): NotificationSeverity {
  if (typeof value !== "string") {
    return "info";
  }
  return ALLOWED_SEVERITIES.includes(value as NotificationSeverity)
    ? (value as NotificationSeverity)
    : "info";
}

function createId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `ntf_${Math.random().toString(36).slice(2, 10)}${Date.now()}`;
}

function normaliseTimestamp(input?: string | Date) {
  if (!input) {
    return new Date().toISOString();
  }
  if (input instanceof Date) {
    return new Date(input.getTime()).toISOString();
  }
  const parsed = new Date(input);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }
  return parsed.toISOString();
}

function reducer(state: NotificationRecord[], action: NotificationAction): NotificationRecord[] {
  switch (action.type) {
    case "push": {
      const next = [action.notification, ...state];
      return next.length > MAX_NOTIFICATIONS ? next.slice(0, MAX_NOTIFICATIONS) : next;
    }
    case "mark-all-read": {
      if (state.every(notification => notification.read)) {
        return state;
      }
      return state.map(notification => (notification.read ? notification : { ...notification, read: true }));
    }
    case "mark-read": {
      return state.map(notification =>
        notification.id === action.id ? { ...notification, read: true } : notification,
      );
    }
    case "remove": {
      return state.filter(notification => notification.id !== action.id);
    }
    case "hydrate": {
      if (!action.notifications.length) {
        return state;
      }
      const existingById = new Map(state.map(notification => [notification.id, notification]));
      const merged = action.notifications.map(notification => {
        const existing = existingById.get(notification.id);
        if (existing) {
          existingById.delete(notification.id);
          return { ...notification, read: existing.read };
        }
        return notification;
      });
      for (const leftover of existingById.values()) {
        merged.push(leftover);
      }
      return merged
        .sort((a, b) => (a.timestamp > b.timestamp ? -1 : a.timestamp < b.timestamp ? 1 : 0))
        .slice(0, MAX_NOTIFICATIONS);
    }
    default:
      return state;
  }
}

function alertToNotification(alert: EmergencyAlertResponse): NotificationRecord {
  const categories = Array.isArray(alert.categories) ? alert.categories : [];
  const timestamp = normaliseTimestamp(alert.triggeredAt ?? alert.receivedAt ?? undefined);
  const severity: NotificationSeverity = categories.includes("VECTOR_SURVEILLANCE_ALERT") ? "critical" : "warning";
  const message = alert.comment?.trim() || "Vector surveillance outcome flagged above threshold.";
  const metadata: Record<string, unknown> = {
    alertId: alert.id,
    categories,
  };
  return {
    id: alert.id,
    title: "Vector surveillance alert",
    message,
    severity,
    category: "vector-surveillance",
    metadata,
    href: undefined,
    timestamp,
    read: false,
  };
}

function readStoredNotifications(): NotificationRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as NotificationRecord[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(item =>
        item && typeof item === "object" && typeof item.id === "string" && typeof item.title === "string",
      )
      .map(item => ({
        ...item,
        read: Boolean(item.read),
        severity: normaliseSeverity((item as NotificationRecord).severity),
        timestamp: normaliseTimestamp(item.timestamp),
      }));
  } catch {
    return [];
  }
}

function persistNotifications(notifications: NotificationRecord[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
  } catch {
    // Ignore storage failures (private mode, quota exceeded, etc.)
  }
}

export function NotificationCenterProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(reducer, [], readStoredNotifications);
  const facilityAccess = useFacilityAccessOrNull();

  React.useEffect(() => {
    persistNotifications(state);
  }, [state]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let cancelled = false;

    const fetchAlerts = async () => {
      try {
        const alerts = await emergencyAlertsApi.listRecent({
          facilityId: facilityAccess?.facilityId ?? undefined,
          limit: 30,
        });
        if (cancelled) {
          return;
        }
        if (!alerts || alerts.length === 0) {
          return;
        }
        const notifications = alerts.map(alertToNotification);
        dispatch({ type: "hydrate", notifications });
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Failed to synchronise emergency alerts", error);
        }
      }
    };

    void fetchAlerts();
    const interval = window.setInterval(() => {
      void fetchAlerts();
    }, 60_000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [facilityAccess?.facilityId, dispatch]);

  const push = React.useCallback(
    (payload: NotificationPayload): NotificationRecord => {
      const timestamp = normaliseTimestamp(payload.triggeredAt);
      const notification: NotificationRecord = {
        id: payload.id || createId(),
        title: payload.title,
        message: payload.message,
        severity: payload.severity || "info",
        category: payload.category,
        metadata: payload.metadata,
        href: payload.href,
        timestamp,
        read: false,
      };
      dispatch({ type: "push", notification });
      return notification;
    },
    [],
  );

  const markAllRead = React.useCallback(() => {
    dispatch({ type: "mark-all-read" });
  }, []);

  const markRead = React.useCallback((id: string) => {
    dispatch({ type: "mark-read", id });
  }, []);

  const remove = React.useCallback((id: string) => {
    dispatch({ type: "remove", id });
  }, []);

  const unreadCount = React.useMemo(() => state.reduce((count, item) => count + (item.read ? 0 : 1), 0), [state]);

  const value = React.useMemo<NotificationCenterValue>(() => ({
    notifications: state,
    unreadCount,
    push,
    markAllRead,
    markRead,
    remove,
  }), [state, unreadCount, push, markAllRead, markRead, remove]);

  return <NotificationCenterContext.Provider value={value}>{children}</NotificationCenterContext.Provider>;
}

export function useNotificationCenter() {
  const context = React.useContext(NotificationCenterContext);
  if (!context) {
    throw new Error("useNotificationCenter must be used within a NotificationCenterProvider");
  }
  return context;
}
